import { useEffect, useMemo } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { styled } from '@mui/material';

import { useI18n } from '@app-velocity/i18n';
import {
  Button,
  ButtonsContainer,
  Form,
  Modal,
  TextField,
  useModalWrapperContext,
} from '@app-velocity/ui';
import { ClassValidatorResolver } from '@app-velocity/validators';

import { useRenameFigmaReportMutation } from '../../../../query/useRenameFigmaReportMutation';
import {
  RenameFigmaReportDto,
} from '../../dto/RenameFigmaReportDto';

const StyledContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));


type RenameFigmaReportModalData = {
  id: string;
  displayName: string;
  workspaceId: string;
};

const isRenameFigmaReportModalData = (
  data: unknown
): data is RenameFigmaReportModalData => {
  if (!data || typeof data !== 'object') return false;

  const casted = data as Partial<RenameFigmaReportModalData>;

  return (
    typeof casted.id === 'string' &&
    !!casted.id &&
    typeof casted.displayName === 'string' &&
    typeof casted.workspaceId === 'string' &&
    !!casted.workspaceId
  );
};


export const RenameFigmaReportModalContent = () => {
  const { t } = useI18n();
  const { modalData, openedModalId, closeModal } = useModalWrapperContext();

  const { id, displayName, workspaceId } = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isRenameFigmaReportModalData(currentModalData)) {
      return currentModalData;
    }

    return { id: null, displayName: '', workspaceId: null };
  }, [modalData, openedModalId]);

  const methods = useForm<RenameFigmaReportDto>({
    resolver: ClassValidatorResolver(RenameFigmaReportDto),
    defaultValues: {
      displayName: displayName || '',
    },
  });

  const {
    control,
    handleSubmit,
  } = methods;

  const {
    mutate: renameReport,
    isPending,
    isSuccess,
  } = useRenameFigmaReportMutation(workspaceId!);

  useEffect(() => {
    if (!isSuccess) return;

    closeModal();
  }, [isSuccess, closeModal]);

  const onSubmit = (values: RenameFigmaReportDto) => {
    if (!id) return;

    renameReport({
      prototypeId: id,
      displayName: values.displayName,
    });
  };

  return (
    <Modal>
      <Modal.Header title="prototypes:rename_report.title" />
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Content>
            <StyledContent>
              <Form.FieldContainer>
                <Form.Label>{t('prototypes:rename_report.new_name')}</Form.Label>
                <TextField
                  control={control}
                  name="displayName"
                  placeholder={t('prototypes:rename_report.new_name_placeholder')}
                  fullWidth
                />
              </Form.FieldContainer>
            </StyledContent>
          </Modal.Content>
          <Modal.Controls>
            <ButtonsContainer>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                type="button"
                onClick={closeModal}
                disabled={isPending}
              >
                {t('shared:cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                type="submit"
                disabled={isPending}
              >
                {t('shared:save')}
              </Button>
            </ButtonsContainer>
          </Modal.Controls>
        </Form>
      </FormProvider>
    </Modal>
  );
};
